import { useNavigate, useLocation } from 'react-router-dom'
import { ShoppingBag, Heart, User } from 'lucide-react'
import { useCartContext } from '../../contexts/CartContext'

interface NavItem {
  path: string
  label: string
  icon: React.ComponentType<{ className?: string }>
}

const navItems: NavItem[] = [
  { path: '/products', label: 'Shop', icon: ShoppingBag },
  { path: '/cart', label: 'Favorites', icon: Heart },
  { path: '/profile', label: 'Profile', icon: User },
]

export default function BottomNav() {
  const navigate = useNavigate()
  const location = useLocation()
  const { getTotalItems } = useCartContext()
  const cartCount = getTotalItems()

  // Determine active tab
  const getActiveTab = (): string => {
    const path = location.pathname
    if (path === '/' || path.startsWith('/products') || path.startsWith('/product')) return '/products'
    if (path.startsWith('/cart') || path.startsWith('/favorites')) return '/cart'
    if (path.startsWith('/profile') || path.startsWith('/settings')) return '/profile'
    return ''
  }

  const activeTab = getActiveTab()
  const isSecondaryPage = activeTab === '' // hide nav on deep pages

  if (isSecondaryPage) return null

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50"
      style={{
        backgroundColor: 'var(--tg-theme-bottom-bar-bg-color, #ffffff)',
        borderTop: '1px solid var(--tg-theme-section-separator-color, rgba(0,0,0,0.08))',
        paddingBottom: 'var(--safe-area-bottom, 16px)',
      }}
    >
      <div className="flex items-center justify-around h-14 px-2 max-w-lg mx-auto">
        {navItems.map((item) => {
          const isActive = activeTab === item.path
          const Icon = item.icon

          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="relative flex flex-col items-center justify-center flex-1 h-full
                transition-all duration-200 active:scale-90"
              style={{
                color: isActive
                  ? 'var(--tg-theme-button-color, #007aff)'
                  : 'var(--tg-theme-hint-color, #999)',
              }}
            >
              <div className="relative">
                <div className="transition-transform duration-200"
                  style={{ transform: isActive ? 'scale(1.1)' : 'scale(1)' }}>
                  <Icon className="w-6 h-6" />
                </div>

                {/* Cart badge */}
                {item.path === '/cart' && cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px]
                    flex items-center justify-center rounded-full text-[10px] font-bold
                    text-white px-1"
                    style={{ backgroundColor: 'var(--tg-theme-destructive-text-color, #ff3b30)' }}
                  >
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}

                {/* Active indicator dot */}
                {isActive && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                    style={{ backgroundColor: 'var(--tg-theme-button-color, #007aff)' }}
                  />
                )}
              </div>

              <span className="text-[11px] font-medium mt-1 leading-none">
                {item.label}
              </span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}
